import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import jwtDecode from 'jwt-decode'

type AutomationTab = 'automations' | 'connections' | 'integration' | 'documentation'
type ViewMode = 'board' | 'table'
type SortBy = 'phase' | 'name_asc' | 'name_desc' | 'recent' | 'oldest' | 'urgency' | 'deadline'

interface DecodedToken {
  sub: number
  tenantId: number
  exp: number
  iat: number
}

interface PipelineFilters {
  keyword: string
  urgency: boolean
  dueDate: {
    notExpired: boolean
    nearExpiry: boolean
    expired: boolean
  }
  assignedUsers: number[]
  tags: number[]
}

interface FilterUser {
  id: number
  name: string
  picture?: string
}

interface FilterTag {
  id: number
  name: string
  color?: string
}

interface PipelineContextType {
  isConfiguringPipeline: boolean
  setIsConfiguringPipeline: React.Dispatch<React.SetStateAction<boolean>>
  viewOrEditTagsModalIsOpen: boolean
  setViewOrEditTagsModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  automationsPageIsOpen: boolean
  setAutomationsPageIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  activeAutomationTab: AutomationTab
  setActiveAutomationTab: React.Dispatch<React.SetStateAction<AutomationTab>>
  pipelineName: string
  setPipelineName: React.Dispatch<React.SetStateAction<string>>
  isCreatingAutomation: boolean
  setIsCreatingAutomation: React.Dispatch<React.SetStateAction<boolean>>
  editingAutomationId: number | null
  setEditingAutomationId: React.Dispatch<React.SetStateAction<number | null>>
  tenantId: number
  setTenantId: React.Dispatch<React.SetStateAction<number>>
  viewMode: ViewMode
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>
  sortBy: SortBy
  setSortBy: React.Dispatch<React.SetStateAction<SortBy>>
  filters: PipelineFilters
  setFilters: React.Dispatch<React.SetStateAction<PipelineFilters>>
  availableUsers: FilterUser[]
  setAvailableUsers: React.Dispatch<React.SetStateAction<FilterUser[]>>
  availableTags: FilterTag[]
  setAvailableTags: React.Dispatch<React.SetStateAction<FilterTag[]>>
  showArchived: boolean
  setShowArchived: React.Dispatch<React.SetStateAction<boolean>>
  isAddingTableCard: boolean
  setIsAddingTableCard: React.Dispatch<React.SetStateAction<boolean>>
}

const PipelineContext = createContext<PipelineContextType | undefined>(undefined)

export const usePipelineContext = () => {
  const context = useContext(PipelineContext)
  if (!context) {
    throw new Error('usePipelineContext must be used within a PipelineProvider')
  }
  return context
}

interface PipelineProviderProps {
  children: ReactNode
}

export const PipelineProvider: React.FC<PipelineProviderProps> = ({ children }) => {
  const [isConfiguringPipeline, setIsConfiguringPipeline] = useState(false)
  const [viewOrEditTagsModalIsOpen, setViewOrEditTagsModalIsOpen] = useState<boolean>(false)
  const [automationsPageIsOpen, setAutomationsPageIsOpen] = useState<boolean>(false)
  const [activeAutomationTab, setActiveAutomationTab] = useState<AutomationTab>('automations')
  const [pipelineName, setPipelineName] = useState<string>('')
  const [isCreatingAutomation, setIsCreatingAutomation] = useState<boolean>(false)
  const [editingAutomationId, setEditingAutomationId] = useState<number | null>(null)
  const [tenantId, setTenantId] = useState<number>(0)
  const [viewMode, setViewMode] = useState<ViewMode>('board')
  const [sortBy, setSortBy] = useState<SortBy>('name_asc')
  const [filters, setFilters] = useState<PipelineFilters>({
    keyword: '',
    urgency: false,
    dueDate: {
      notExpired: false,
      nearExpiry: false,
      expired: false
    },
    assignedUsers: [],
    tags: []
  })
  const [availableUsers, setAvailableUsers] = useState<FilterUser[]>([])
  const [availableTags, setAvailableTags] = useState<FilterTag[]>([])
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [isAddingTableCard, setIsAddingTableCard] = useState<boolean>(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token)
        console.debug('[PipelineContext] Token decoded, tenantId:', decoded.tenantId)
        setTenantId(decoded.tenantId)
      } catch (error) {
        console.error('[PipelineContext] Erro ao decodificar token:', error)
      }
    }
  }, [])

  return (
    <PipelineContext.Provider
      value={{
        isConfiguringPipeline,
        setIsConfiguringPipeline,
        viewOrEditTagsModalIsOpen,
        setViewOrEditTagsModalIsOpen,
        automationsPageIsOpen,
        setAutomationsPageIsOpen,
        activeAutomationTab,
        setActiveAutomationTab,
        pipelineName,
        setPipelineName,
        isCreatingAutomation,
        setIsCreatingAutomation,
        editingAutomationId,
        setEditingAutomationId,
        tenantId,
        setTenantId,
        viewMode,
        setViewMode,
        sortBy,
        setSortBy,
        filters,
        setFilters,
        availableUsers,
        setAvailableUsers,
        availableTags,
        setAvailableTags,
        showArchived,
        setShowArchived,
        isAddingTableCard,
        setIsAddingTableCard,
      }}
    >
      {children}
    </PipelineContext.Provider>
  )
}
